import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Grid,
  Chip,
  LinearProgress,
} from '@mui/material'
import {
  CloudUpload,
  Edit,
  Person,
  Lock,
  Analytics as AnalyticsIcon,
  Delete,
  BugReport,
  FavoriteBorder,
} from '@mui/icons-material'
import { useGetProfileQuery, useUpdateProfileMutation, useChangePasswordMutation, useDeleteAccountMutation, useUploadProfilePictureMutation } from '../store/api/userApi'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { loginSuccess } from '@store/slices/authSlice'
import FeedbackList from '@components/feedback/FeedbackList'
import Analytics from './Analytics'
import Wishlist from './Wishlist'
import PageHeader from '@components/layout/PageHeader'

// FormData type for file uploads
declare global {
  interface FormData {
    append(name: string, value: Blob | string, fileName?: string): void
  }
  interface Blob {
    readonly size: number
    readonly type: string
    slice(): Blob
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
)

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [emailChangePassword, setEmailChangePassword] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const dispatch = useAppDispatch()
  const { user: authUser } = useAppSelector((state) => state.auth)
  const { data: profile, isLoading: profileLoading, isError: profileError, refetch: refetchProfile } = useGetProfileQuery()
  const [updateProfile, { isLoading: updateLoading }] = useUpdateProfileMutation()
  const [changePassword, { isLoading: passwordLoading }] = useChangePasswordMutation()
  const [deleteAccount, { isLoading: deleteLoading }] = useDeleteAccountMutation()
  const [uploadProfilePicture, { isLoading: uploadLoading }] = useUploadProfilePictureMutation()

  React.useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        username: profile.username || '',
      })
    }
  }, [profile])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value })
  }

  const handlePasswordChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({ ...passwordData, [field]: e.target.value })
  }

  const handleProfileUpdate = async () => {
    try {
      const emailChanged = Boolean(profile?.email && formData.email && formData.email !== profile.email)
      if (emailChanged && !emailChangePassword) {
        setMessage({ type: 'error', text: 'Enter your current password to change email' })
        return
      }
      await updateProfile({
        ...formData,
        ...(emailChanged ? { currentPassword: emailChangePassword } : {}),
      }).unwrap()
      setEditMode(false)
      setEmailChangePassword('')
      setMessage({
        type: 'success',
        text: emailChanged
          ? 'Profile saved. Confirm the new address from the email we just sent.'
          : 'Profile updated successfully!',
      })
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.message || 'Failed to update profile' })
    }
  }

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' })
      return
    }

    try {
      const result = await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      }).unwrap()
      if (authUser && result?.accessToken) {
        dispatch(loginSuccess({ user: authUser, token: result.accessToken }))
      }
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setMessage({ type: 'success', text: 'Password changed successfully!' })
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.message || 'Failed to change password' })
    }
  }

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const formData = new FormData()
      formData.append('profilePicture', file)
      
      try {
        const result = await uploadProfilePicture(formData).unwrap()
        await updateProfile({ profileImageUrl: result.url }).unwrap()
        setMessage({ type: 'success', text: 'Profile picture updated!' })
      } catch (_err: any) {
        setMessage({ type: 'error', text: 'Failed to upload profile picture' })
      }
    }
  }

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount().unwrap()
      // Redirect to login or home page after successful deletion
      window.location.href = '/auth/login'
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.message || 'Failed to delete account' })
      setDeleteDialogOpen(false)
    }
  }

  if (profileLoading) return <LinearProgress />

  if (profileError) {
    return (
      <Box maxWidth={1200} mx="auto" p={3}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetchProfile()}>
              Retry
            </Button>
          }
        >
          Failed to load profile. Please try again.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageHeader title="Profile" />

      {message.text && (
        <Alert 
          severity={message.type as 'success' | 'error'} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<Person />} label="Profile" />
          <Tab icon={<Lock />} label="Security" />
          <Tab icon={<AnalyticsIcon />} label="Analytics" />
          <Tab icon={<BugReport />} label="My Reports" />
          <Tab icon={<FavoriteBorder />} label="Wishlist" />
        </Tabs>
      </Box>

      {/* Profile Tab */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  src={profile?.profileImageUrl}
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                />
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={uploadLoading}
                  sx={{ mb: 2 }}
                >
                  Upload Photo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                  />
                </Button>
                <Typography variant="h6">{profile?.username}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile?.email}
                </Typography>
                <Chip
                  label={profile?.subscriptionTier}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Personal Information</Typography>
                  <Button
                    startIcon={editMode ? <></> : <Edit />}
                    onClick={() => setEditMode(!editMode)}
                  >
                    {editMode ? 'Cancel' : 'Edit'}
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName}
                      onChange={handleInputChange('firstName')}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName}
                      onChange={handleInputChange('lastName')}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={formData.username}
                      onChange={handleInputChange('username')}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      disabled={!editMode}
                      type="email"
                    />
                  </Grid>
                  {editMode && profile?.email && formData.email !== profile.email && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current password (required to change email)"
                        type="password"
                        value={emailChangePassword}
                        onChange={(e) => setEmailChangePassword(e.target.value)}
                        autoComplete="current-password"
                      />
                    </Grid>
                  )}
                </Grid>

                {editMode && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleProfileUpdate}
                      disabled={updateLoading}
                      sx={{ mr: 2 }}
                    >
                      {updateLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Security Tab */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Change Password
                </Typography>
                <Box component="form" sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange('currentPassword')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange('newPassword')}
                    margin="normal"
                    helperText="Min 8 characters, with uppercase, lowercase, number, and special character (!@#$%^&*)"
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword')}
                    margin="normal"
                  />
                  <Button
                    variant="contained"
                    onClick={handlePasswordUpdate}
                    disabled={passwordLoading}
                    sx={{ mt: 2 }}
                  >
                    {passwordLoading ? 'Updating...' : 'Change Password'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error">
                  Danger Zone
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Once you delete your account, there is no going back. Please be certain.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Account'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Analytics Tab */}
      <TabPanel value={activeTab} index={2}>
        <Analytics />
      </TabPanel>

      {/* My Reports Tab */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="h6" gutterBottom>My Submitted Reports</Typography>
        <FeedbackList />
      </TabPanel>

      {/* Wishlist Tab */}
      <TabPanel value={activeTab} index={4}>
        <Wishlist embedded />
      </TabPanel>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your account? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Profile
